import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useAuth } from './contexts/AuthContext';
import ClientList from './components/ClientList';
import ClientDetail from './components/ClientDetail';
import WorkflowManagement from './components/WorkflowManagement';
import UserSettings from './components/UserSettings';
import Users from './components/Users';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';

function AppContent() {
  const { instance, accounts, inProgress } = useMsal();
  const { userName, userRole, isAdmin, loading } = useAuth();

  const handleLogout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: '/',
    });
  };

  // Show login page if not authenticated
  if (accounts.length === 0) {
    return <Login />;
  }

  // Show loading while MSAL is processing or checking role
  if (loading || inProgress !== 'none') {
    return (
      <div className="loading-screen">
        <div className="card">
          <h2>Loading...</h2>
          <p>Checking permissions...</p>
        </div>
      </div>
    );
  }

  // Show error if authentication failed (only after loading is complete)
  if (!userRole) {
    return (
      <div className="loading-screen">
        <div className="card">
          <h2>Access Denied</h2>
          <p>You are not authorized to access this application.</p>
          <p>Please contact your administrator to be added to the required security group.</p>
          <button className="btn btn-primary" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <nav className="navbar">
          <div className="nav-container">
            <h1 className="nav-logo">Plant Patrol</h1>
            <ul className="nav-menu">
              <li className="nav-item">
                <Link to="/" className="nav-link">
                  Clients
                </Link>
              </li>
              {isAdmin() && (
                <>
                  <li className="nav-item">
                    <Link to="/workflow" className="nav-link">
                      Workflow
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link to="/users" className="nav-link">
                      Users
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link to="/settings" className="nav-link">
                      Settings
                    </Link>
                  </li>
                </>
              )}
            </ul>
            <div className="nav-user-info">
              <span className="user-name">{userName}</span>
              <span className="user-role">{userRole}</span>
              <button className="btn btn-sm btn-logout" onClick={handleLogout}>
                Logout
              </button>
            </div>
          </div>
        </nav>

        <div className="container">
          <Routes>
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <ClientList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/:id"
              element={
                <ProtectedRoute>
                  <ClientDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workflow"
              element={
                <ProtectedRoute adminOnly={true}>
                  <WorkflowManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute adminOnly={true}>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute adminOnly={true}>
                  <UserSettings />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
    </Router>
  );
}

function App() {
  return <AppContent />;
}

export default App;
